<template>
  <MetricsSummaryWithLoader
    :loading="loading"
    :number-of-summary-skeletons="4"
    totals-label-width="208px"
    :summaries="summaries"
    :project-name="projectName"
    :date-range="dateRange"
  />
</template>

<script setup>
import MetricsSummaryWithLoader from '@/components/insights/Metrics/MetricsSummaryWithLoader.vue';
import { computed } from 'vue';
import { isEmpty } from 'lodash-es';
import { formatTooltipValue } from '@/helpers/formatters';
import { useColors } from '@/composables/colors';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
  tooltips: {
    type: Object,
    default: () => ({}),
  },
  projectName: {
    type: String,
    default: '',
  },
  dateRange: {
    type: Object,
    default: () => ({}),
  },
});

const { brandPrimary } = useColors();

const summaries = computed(() => {
  if (isEmpty(props.data)) return [];

  const {
    female,
    male,
    unknown,
    declined,
  } = props.data || {};

  const totalApplications = female.review
    + male.review
    + unknown.review
    + declined.review;
  const totalHires = female.hired + male.hired + unknown.hired + declined.hired;

  const baseSummaries = [
    {
      id: 'female_applied',
      tooltipKey: 'gender-female-apps',
      label: 'Female Applications',
      color: brandPrimary.value,
      value:
        typeof female?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((female.review / totalApplications) * 100, 'percent')
          : '-',
    },
    {
      id: 'female_hired',
      tooltipKey: 'gender-female-hires',
      label: 'Female Hires',
      color: brandPrimary.value,
      value:
        typeof female?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((female.hired / totalHires) * 100, 'percent')
          : '-',
    },
    {
      id: 'male_applied',
      tooltipKey: 'gender-male-apps',
      label: 'Male Applications',
      color: brandPrimary.value,
      value:
        typeof male?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((male.review / totalApplications) * 100, 'percent')
          : '-',
    },
    {
      id: 'male_hired',
      tooltipKey: 'gender-male-hires',
      label: 'Male Hires',
      color: brandPrimary.value,
      value:
        typeof male?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((male.hired / totalHires) * 100, 'percent')
          : '-',
    },
  ];

  return baseSummaries.map((summary) => {
    const { body_r: html, mode } = props.tooltips?.[summary.tooltipKey] || {};
    return {
      ...summary,
      tooltip: { html, mode, key: summary.tooltipKey },
    };
  });
});
</script>
